import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
	selectAllPages,
	selectCurrentPageId,
	selectIsPageValid,
} from "../reducers";
import {
	sessionEdited,
	selectFormStatus,
	selectFormError,
	errorEdited,
	selectSession,
} from "../reducers/formSlice";
import { message, notification } from "antd";

const messageKey = "page-updatable";

export const useNotification = () => {
	const dispatch = useDispatch();
	const status = useSelector(selectFormStatus);
	const error = useSelector(selectFormError);
	const validationMessage = useSelector(selectSession)?.validationMessage;

	useEffect(() => {
		//destroy any active messages that displayed from a previous status
		message.destroy(messageKey);

		if (status === "validation error") {
			const onClose = () => {
				dispatch(
					sessionEdited({ status: "validation error dismissed" })
				);
			};
			notification.warning({
				message: "Validation error",
				description: validationMessage,
				duration: 0,
				onClose,
			});
		}

		if (error) {
			notification.error({
				message: error?.title,
				description: error?.message,
				duration: 0,
				key: error?.title, //ensure only one error message per type can be shown
				onClose: () => dispatch(errorEdited(null)),
			}); //todo: unset errors
		}
	}, [dispatch, status, error, validationMessage]);
};

export const useNavigation = ({ save }) => {
	const dispatch = useDispatch();
	const pages = useSelector(selectAllPages);
	const currentPageId = useSelector(selectCurrentPageId);
	const currentPageIndex = pages.findIndex((p) => p.id === currentPageId);
	const currentPage = pages[currentPageIndex];
	const prevPageId =
		currentPageIndex > 0 ? pages[currentPageIndex - 1]?.id : null;
	const nextPageId =
		currentPageIndex < pages.length
			? pages[currentPageIndex + 1]?.id
			: null;

	const pageType = currentPage?.type || null;
	const isPageValid = useSelector(selectIsPageValid);

	const decrementPage = () => {
		if (prevPageId) {
			dispatch(sessionEdited({ currentPage: prevPageId }));
			dispatch(sessionEdited({ status: "idle" }));
		}
	};

	const incrementPage = async () => {
		if (nextPageId) {
			if (!isPageValid) {
				dispatch(sessionEdited({ status: "validation error" }));
				return;
			} else {
				//save between navigation
				const success = await save({ isSilentSave: true });
				if (success)
					dispatch(sessionEdited({ currentPage: nextPageId }));
			}
		}
	};

	const prevButtonVisible = prevPageId ? true : false;
	const nextButtonVisible = nextPageId ? true : false;

	return [
		decrementPage,
		incrementPage,
		currentPage,
		currentPageId,
		pageType,
		prevButtonVisible,
		nextButtonVisible,
		isPageValid,
	];
};
